/**
 * Environment detection and configuration for CarbonWize landing page
 * Supports 3 environments: dev, uat, prod
 */

export type Environment = 'development' | 'uat' | 'production';

export interface EnvironmentConfig {
  environment: Environment;
  signInUrl: string;
}

/**
 * Detects the current environment from URL hostname
 *
 * Detection rules:
 * - dev.carbonwize.io, localhost, 127.0.0.1 → development
 * - uat.carbonwize.io → uat
 * - carbonwize.io, www.carbonwize.io, or any other → production
 */
export function detectEnvironment(): Environment {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    return 'production';
  }

  const hostname = window.location.hostname;

  // Development: dev.carbonwize.io, localhost, 127.0.0.1
  if (
    hostname.startsWith('dev.') ||
    hostname === 'localhost' ||
    hostname === '127.0.0.1'
  ) {
    return 'development';
  }

  // UAT: uat.carbonwize.io
  if (hostname.startsWith('uat.')) {
    return 'uat';
  }

  // Production: carbonwize.io, www.carbonwize.io, or any other
  return 'production';
}

/**
 * Gets environment-specific configuration
 * Returns URLs and environment identifier for client-side use
 */
export function getEnvironmentConfig(): EnvironmentConfig {
  const env = detectEnvironment();

  // Environment-specific Sign In URLs
  const signInUrl = env === 'development'
    ? 'https://dev.carbonwize.io'
    : env === 'uat'
      ? 'https://uat.carbonwize.io'
      : 'https://app.carbonwize.io';

  return {
    environment: env,
    signInUrl,
  };
}

/**
 * Helper function to get the environment parameter for API calls
 * Returns the environment string to pass to Supabase functions
 */
export function getEnvironmentParam(): string {
  return detectEnvironment();
}
